import { useId } from 'react';

export function instrumentProgress(value, goal) {
  return Number.isFinite(value) && Number.isFinite(goal) && goal > 0
    ? Math.max(0, Math.min(1, value / goal)) : 0;
}

const point = (radius, angle) => [200 + radius * Math.cos(angle), 207 - radius * Math.sin(angle)];
const segment = (start, end, outer = 190, inner = 177) => {
  const a = point(outer, start), b = point(outer, end), c = point(inner, end), d = point(inner, start);
  return `M${a} A${outer} ${outer} 0 0 1 ${b} L${c} A${inner} ${inner} 0 0 0 ${d} Z`;
};

/** Only variable markings are drawn here. The physical housing is the artwork underneath. */
export function PrecisionMonthlyInstrument({ value, goal }) {
  const id = useId().replace(/:/g, '');
  const valid = Number.isFinite(value) && Number.isFinite(goal) && goal > 0;
  const progress = instrumentProgress(value, goal);
  const needleAngle = Math.PI * (1 - progress);
  const tip = point(190, needleAngle), base = point(135, needleAngle);
  return <><svg className="precision-monthly-scale" viewBox="0 0 400 220" preserveAspectRatio="none" aria-hidden="true">
    <defs>
      <linearGradient id={`${id}-segments`} x1="0" y1="1" x2=".8" y2="0">
        <stop stopColor="#72532c"/><stop offset=".32" stopColor="#c2a269"/><stop offset=".72" stopColor="#efd596"/><stop offset="1" stopColor="#fff0c7"/>
      </linearGradient>
      <linearGradient id={`${id}-needle`}><stop stopColor="#a77731"/><stop offset=".48" stopColor="#fff5cb"/><stop offset="1" stopColor="#bc934e"/></linearGradient>
      <radialGradient id={`${id}-glass`} cx=".36" cy="0" r="1"><stop stopColor="#ffffff" stopOpacity=".05"/><stop offset=".7" stopColor="#ffffff" stopOpacity="0"/></radialGradient>
    </defs>
    <path d="M8 207 A192 192 0 0 1 392 207" fill={`url(#${id}-glass)`} stroke="#d1aa6430" strokeWidth="1"/>
    <path d="M14 207 A186 186 0 0 1 386 207" fill="none" stroke="#ffedbd16" strokeWidth="1"/>
    {Array.from({ length: 50 }, (_, i) => {
      const start = Math.PI * (1 - i / 50) - .004, end = Math.PI * (1 - (i + 1) / 50) + .004;
      return <path key={i} d={segment(start, end)} fill={valid && (i + .5) / 50 <= progress ? `url(#${id}-segments)` : '#5c4a30'} opacity={valid && (i + .5) / 50 <= progress ? 1 : .32}/>;
    })}
    {Array.from({ length: 51 }, (_, i) => {
      const angle = Math.PI * (1 - i / 50), major = i % 5 === 0;
      const from = point(174, angle), to = point(major ? 154 : 164, angle);
      return <line key={i} x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]} stroke={major ? '#ebd4a0' : '#ae956b'} strokeOpacity={major ? .82 : .54} strokeWidth={major ? 1.4 : .7}/>;
    })}
    {valid && <g className="precision-needle" data-progress={progress}>
      <line x1={base[0]} y1={base[1]} x2={tip[0]} y2={tip[1]} stroke="#000" strokeWidth="8" opacity=".7" transform="translate(2 3)"/>
      <line x1={base[0]} y1={base[1]} x2={tip[0]} y2={tip[1]} stroke={`url(#${id}-needle)`} strokeWidth="4"/>
      <line x1={base[0] - 1} y1={base[1]} x2={tip[0] - 1} y2={tip[1]} stroke="#fff3bd" strokeWidth=".8"/>
    </g>}
  </svg><div className="precision-range-labels" aria-hidden="true"><span>0</span>{Number.isFinite(goal) && goal > 0 && <span>{Math.round(goal).toLocaleString('de-DE')}</span>}</div></>;
}

export function PrecisionMiniInstrument({ value, goal }) {
  const id = useId().replace(/:/g, '');
  const valid = Number.isFinite(value), hasGoal = Number.isFinite(goal) && goal > 0;
  const progress = instrumentProgress(value, goal);
  return <svg className="precision-mini-scale" viewBox="0 0 100 70" aria-hidden="true">
    <defs>
      <linearGradient id={`${id}-rim`} x1="0" y1="1" x2=".65" y2="0"><stop stopColor="#866a37"/><stop offset=".5" stopColor="#d5b576"/><stop offset="1" stopColor="#fff0bb"/></linearGradient>
      <radialGradient id={`${id}-glass`} cx=".4" cy=".1" r=".9"><stop stopColor="#283038" stopOpacity=".48"/><stop offset="1" stopColor="#020405" stopOpacity=".1"/></radialGradient>
    </defs>
    <path d="M8 58 A42 42 0 0 1 92 58" fill={`url(#${id}-glass)`} stroke="#030405" strokeWidth="11"/>
    <path d="M8 58 A42 42 0 0 1 92 58" fill="none" stroke="#31393f" strokeWidth="8"/>
    <path d="M8 58 A42 42 0 0 1 92 58" fill="none" stroke="#ffffff15" strokeWidth=".6" transform="translate(0 -4)"/>
    <path d="M8 58 A42 42 0 0 1 92 58" fill="none" stroke={`url(#${id}-rim)`} strokeWidth="8" pathLength="100" strokeDasharray={`${progress * 100} 100`}/>
    <path d="M14 58 A36 36 0 0 1 86 58" fill="none" stroke="#bd98592b" strokeWidth=".6"/>
    <text x="50" y="56" textAnchor="middle">{valid ? Math.round(value).toLocaleString('de-DE') : '–'}<tspan className="precision-mini-goal"> / {hasGoal ? Math.round(goal).toLocaleString('de-DE') : '–'}</tspan></text>
  </svg>;
}
